import React from "react";
import {
	ImageComponent,
	BasicTable,
	ExpandableCellContent,
} from "../../../../../../components";
import placeholderImage from "../../../../../../assets/empty-image.png";
import useHostName from "../../../../../../hooks/useHostName";
import { getFontAwesomeIcon, formatCurrency } from "../../../../../../utils";

const prudentialHeaders = [
	{ id: "col1", Header: "Line", headerClasses: "text-left" },
	{ id: "col2", Header: "Part Number", headerClasses: "text-left" },
	{ id: "col3", Header: "Description", headerClasses: "text-left" },
];

type TableData = {
	uuid: string;
	lineNumber: number;
	quantity: number;
	price: number;
	_qtyFulfilled: number;
	_qtyBilled: number;
	_extPrice: number;
	Bundles: {
		name: null;
		uuid: null;
	};
	BundleItems: {
		uuid: null;
		isRequired: null;
	};
	BundleItemGroups: {
		name: null;
	};
	Items: {
		partNumber: string;
		skuNumber: null;
		upcCode: null;
		description: string;
		inventoryType: string;
	};
	Manufacturers: {
		name: string;
	};
	PriceTypes: {
		name: null;
	};
};

type DataRow = {
	col1: string | number | boolean | JSX.Element;
	col2: string | number | boolean | JSX.Element;
	col3: string | number | boolean | JSX.Element;
	col4: string | number | boolean | JSX.Element;
	col5: string | number | boolean | JSX.Element;
	col6: string | number | boolean | JSX.Element;
	col7: string | number | boolean | JSX.Element;
	col8: string | number | boolean | JSX.Element;
	col9: string | number | boolean | JSX.Element;
	col10: string | number | boolean | JSX.Element;
	col11: string | number | boolean | JSX.Element;
	col12: string | number | boolean | JSX.Element;
};

type OrderItemsTableProps = {
	data: TableData[];
	isLoading: boolean;
	toggleModal: () => void;
	dynamicModalChange: any;
	currencyCharacter: string;
	fields?: { [key: string]: any };
};

const OrderItemsTable: React.FC<OrderItemsTableProps> = ({
	data,
	toggleModal,
	dynamicModalChange,
	currencyCharacter,
	fields,
}) => {
	const hostName = useHostName().hostName;

	const clientHasFulfillmentModal = hostName !== "COMPASS";
	const orderHasSerializedItems = !!data?.some(
		(item) => item?.Items?.inventoryType === "SERIALIZED",
	);
	const showFulfillmentModal =
		clientHasFulfillmentModal && orderHasSerializedItems;

	const manufacturerColumnClasses = `flex items-center text-sm relative font-omnes-regular font-light ${
		hostName === "PRUDENTIAL" ? "max-w-[90%]" : " "
	}`;
	const descriptionColumnClasses = `flex items-center justify-between text-sm font-omnes-regular font-light break-words ${
		hostName === "PRUDENTIAL" ? "max-w-[90%]" : " "
	}`;
	const quantityColumnsClasses = `flex items-center text-sm relative font-omnes-regular max-w-96 min-w-[70px] font-light  ${
		hostName === "PRUDENTIAL" ? "max-w-[90%]" : " "
	}`;
	const priceColumnClasses = `flex items-center text-sm relative font-omnes-regular max-w-96 min-w-[90px] font-light  ${
		hostName === "PRUDENTIAL" ? "max-w-[90%]" : " "
	}`;

	// CLASSES:
	const tableHeaderContainerClasses = `flex justify-start items-center ${
		hostName === "PRUDENTIAL" ? "pb-2" : ""
	}`;

	const tableData = React.useMemo(() => {
		return data?.map((item: any, index: number) => {
			const uuid = item?.Items?.uuid || item?.uuid;
			return {
				uuid,
				isRowModalEnabled: item.Items.inventoryType === "SERIALIZED",

				col1: <div className="flex justify-end">{index + 1}</div>,
				col2: (
					<div className="flex items-center justify-start w-fit min-w-40">
						<ImageComponent
							imageUrl={placeholderImage}
							altText="Sample Image"
							className=""
							width={20}
							height={10}
						/>
						<div className="pl-2">{item?.Items.partNumber}</div>
					</div>
				),

				col3: item.Items.skuNumber || "—",
				col4: (
					<>
						<ExpandableCellContent
							columnData={item?.Manufacturers?.name || "—"}
							columnDataLength={15}
							onHoverColumnDataLength={10}
							minWidth="min-w-[150px] max-w-[150px]"
							notShortenedClasses={manufacturerColumnClasses}
						/>
					</>
				),
				col5: (
					<>
						<ExpandableCellContent
							columnData={item?.Items.description || "—"}
							columnDataLength={40}
							onHoverColumnDataLength={35}
							minWidth="min-w-[325px] max-w-[325px]"
							notShortenedClasses={descriptionColumnClasses}
						/>
					</>
				),
				col6: (
					<div className={`${quantityColumnsClasses} justify-end`}>
						{Number(item?.quantity)}
					</div>
				),
				col7: (
					<div className={`${quantityColumnsClasses} justify-end`}>
						{Number(item?._qtyFulfilled)}
					</div>
				),
				col8: (
					<div className={`${quantityColumnsClasses} justify-end`}>
						{Number(item?._qtyBilled)}
					</div>
				),
				col9: (
					<div
						className={`${priceColumnClasses} flex justify-between`}
					>
						<span className="pr-2">{currencyCharacter}</span>
						<span>{formatCurrency(item?.price)}</span>
					</div>
				),
				col10: (
					<div
						className={`${priceColumnClasses} flex justify-between`}
					>
						<span className="pr-2">{currencyCharacter}</span>
						<span>{formatCurrency(item?._extPrice)}</span>
					</div>
				),
			};
		});
	}, [data]);

	const defaultHeaderData = React.useMemo(() => {
		const h = fields?.labels?.itemsTableHeaders;
		return [
			{
				id: "col1",
				Header: h?.line || "Line",
				headerClasses: "text-right",
			},
			{
				id: "col2",
				Header: h?.partNumber || "Part Number",
				headerClasses: "text-left",
			},
			{ id: "col3", Header: h?.sku || "SKU", headerClasses: "text-left" },
			{
				id: "col4",
				Header: h?.manufacturer || "Manufacturer",
				headerClasses: "text-left",
			},
			{
				id: "col5",
				Header: h?.description || "Description",
				headerClasses: "text-left",
			},
			{
				id: "col6",
				Header: h?.ordered || "Ordered",
				headerClasses: "text-right",
			},
			{
				id: "col7",
				Header: h?.fulfilled || "Fulfilled",
				headerClasses: "text-right",
				columnHasModalButton: showFulfillmentModal,
			},
			{
				id: "col8",
				Header: h?.billed || "Billed",
				headerClasses: "text-right",
			},
			{
				id: "col9",
				Header: h?.unitPrice || "Unit Price",
				headerClasses: "text-right",
			},
			{
				id: "col10",
				Header: h?.extPrice || "Ext. Price",
				headerClasses: "text-right",
			},
		];
	}, [fields]);

	return (
		<section>
			<div className="overflow-auto font-omnes-regular">
				<div className={tableHeaderContainerClasses}>
					<h2 className="font-omnes-medium text-xl text-navy-500 leading-6">
						{fields?.labels?.itemsSectionTitle}
					</h2>
				</div>
				<BasicTable
					data={tableData}
					headers={
						hostName != "PRUDENTIAL"
							? defaultHeaderData
							: prudentialHeaders
					}
					hasHeaderSpan={hostName != "PRUDENTIAL" ? true : false}
					tableBottomBorderClasses="border-b-1 border-navy-250"
					headerSpanText={fields?.labels?.quantityHeaderSpan}
					tableWidth={hostName === "PRUDENTIAL" ? "w-1/2" : "w-full"}
					quantityLabel={fields?.labels?.itemsQuantityLabel}
					quantityLabelClasses="text-base"
					tableContainerHeight="overflow-x-scroll"
					headerBackgroundColor="bg-navy-100"
					headerTextColor="text-black"
					headerFontSize="text-sm"
					headerBorderColor="border-navy-250"
					headerBorderWidth="border-1 "
					headerFontFamily=""
					tableBorderColor="border-transparent"
					rowBackgroundColor="bg-white"
					rowHoverBackgroundColor=""
					rowBorderWidth="border-1"
					rowBorderColor="border-navy-200"
					rowHoverCursor=""
					cellFontSize="text-sm"
					cellPadding="py-3 px-2"
					showQuantity={true}
					toggleModal={toggleModal}
					dynamicModalChange={dynamicModalChange}
				/>
			</div>
		</section>
	);
};

export default OrderItemsTable;
